import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MoonStar, Clock, ShieldCheck, Sparkles, Tag } from "lucide-react";
import { easeOutExpo } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const line = (text, delay, accent = false) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${accent ? "italic text-petrol" : ""}`}
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, ease: easeOutExpo, delay }}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const chips = [
    { icon: Clock, label: "Illat ja viikonloput" },
    { icon: MoonStar, label: "Yöhoito sovittaessa" },
    { icon: Sparkles, label: "Extemporekin: kysy rohkeasti" },
  ];

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 sm:pt-32 lg:min-h-screen lg:pt-36" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -right-40 top-0 h-[36rem] w-[36rem] rounded-full bg-petrol-soft blur-3xl" />
        <div className="absolute -left-32 top-1/3 h-[34rem] w-[34rem] rounded-full bg-greige blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-16 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-24">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05, ease: easeOutExpo }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-line bg-cream/80 px-4 py-2 backdrop-blur"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-beige-deep" />
            <span className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-petrol/80">
              Lastenhoitaja · Helsinki · Espoo · Vantaa
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-serif text-[clamp(3rem,8.5vw,6.25rem)] font-medium leading-[1.02] tracking-[-0.01em] text-petrol-night"
          >
            {line("Sinäkin ansaitset", 0.2)}
            {line("vapaan illan.", 0.38, true)}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6, ease: easeOutExpo }}
            className="mt-7 max-w-xl text-base leading-relaxed text-mutedink sm:text-lg"
          >
            Luotettavaa ja asiantuntevaa lastenhoitoa arkeen, iltoihin,
            viikonloppuihin ja sovittaessa myös yöksi.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: easeOutExpo }}
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <button
              data-testid="hero-cta-contact-button"
              onClick={() => scrollToId("yhteydenotto")}
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-petrol px-8 py-4 text-base font-semibold text-cream shadow-[0_18px_40px_-16px_rgba(18,58,62,0.65)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-petrol-dark"
            >
              Ota yhteyttä
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
            <button
              data-testid="hero-cta-services-button"
              onClick={() => scrollToId("palvelut")}
              className="inline-flex items-center justify-center gap-3 rounded-full border border-petrol/25 px-8 py-4 text-base font-semibold text-petrol transition-all duration-300 hover:border-petrol hover:bg-petrol/5"
            >
              Tutustu palveluihin
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: easeOutExpo }}
            className="mt-8 inline-flex items-center gap-3 rounded-full border border-beige-border bg-beige/30 px-5 py-3"
            data-testid="hero-kotitalousvahennys-badge"
          >
            <Tag className="h-4 w-4 shrink-0 text-beige-deep" strokeWidth={1.8} />
            <span className="text-sm font-medium text-ink/85">
              Kotitalousvähennyskelpoinen, jopa −40 % hoitokuluista verotuksessa
            </span>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: easeOutExpo }}
            className="mt-8 flex flex-wrap gap-x-7 gap-y-3"
          >
            {chips.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2.5 text-sm font-medium text-ink/75">
                <Icon className="h-4 w-4 text-beige-deep" strokeWidth={1.8} />
                {label}
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="lg:col-span-5">
          <motion.div style={{ y: photoY }} className="relative mx-auto w-full max-w-sm lg:max-w-none">
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: 2 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 1.2, delay: 0.5, ease: easeOutExpo }}
              className="relative"
            >
              <div
                data-testid="hero-photo"
                className="overflow-hidden rounded-[2rem] border-[6px] border-white shadow-[0_45px_80px_-35px_rgba(18,58,62,0.5)]"
              >
                <img
                  src="/assets/kaisa-portrait.jpg"
                  alt="Kaisa hymyilee kesäisenä päivänä"
                  className="aspect-[4/5] w-full object-cover object-[50%_30%]"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.15, ease: easeOutExpo }}
                className="absolute -bottom-6 -left-4 flex items-center gap-3 rounded-2xl border border-line bg-cream/95 px-5 py-4 shadow-[0_20px_50px_-20px_rgba(18,58,62,0.35)] backdrop-blur sm:-left-10"
              >
                <ShieldCheck className="h-5 w-5 text-petrol" strokeWidth={1.8} />
                <div>
                  <p className="text-sm font-semibold text-petrol-night">Halutessanne tutustutaan ensin</p>
                  <p className="text-xs text-mutedink">Rauhallinen tapaaminen ennen ensimmäistä hoitokertaa.</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        onClick={() => scrollToId("minusta")}
        className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 items-center gap-2 text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-mutedink/70 transition-colors hover:text-petrol lg:flex"
        aria-label="Vieritä alas"
      >
        Vieritä
        <motion.span animate={{ y: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown className="h-3.5 w-3.5" />
        </motion.span>
      </motion.button>
    </section>
  );
};
